import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { pmApi, STATUS_FLOW } from '../../lib/pmApi';
import { ArrowLeft, CheckCircle2, Circle, Siren } from 'lucide-react';
import PoPanel from '../../components/pm/PoPanel';
import JobTimeline from '../../components/pm/JobTimeline';
import WoAuthorizationPanel from '../../components/pm/WoAuthorizationPanel';

const WorkOrderDetail = () => {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => { pmApi.requestDetail(id).then(setR).catch((e) => setErr(e.response?.data?.detail || e.message)); }, [id]);

  if (err) return <div className="p-6 text-[#EF4444]">{err}</div>;
  if (!r) return <div className="p-6 text-[#A3A3A3]">Loading…</div>;

  const currentIdx = STATUS_FLOW.indexOf(r.status);

  return (
    <div data-testid="pm-wo-detail" className="p-6 max-w-5xl space-y-6">
      <Link to="/pm/work-orders" className="text-[#A3A3A3] text-sm hover:text-white inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Work orders</Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">WORK ORDER</p>
          <h1 className="font-['Outfit'] text-3xl font-bold text-white font-mono">{r.work_order_number}</h1>
          <p className="text-[#A3A3A3] mt-1">{r.property_name} {r.unit_number ? `· Unit ${r.unit_number}` : ''}</p>
        </div>
        <span className={`text-sm font-medium px-3 py-1.5 rounded ${r.priority === 'Emergency' ? 'bg-[#EF4444]/15 text-[#EF4444]' : 'bg-[#D4AF37]/15 text-[#D4AF37]'}`}>
          {r.priority === 'Emergency' && <Siren className="w-4 h-4 inline-block mr-1" />}{r.priority}
        </span>
      </div>

      {/* Timeline */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-5">
        <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em] mb-4">PROGRESS</p>
        <div className="grid grid-cols-9 gap-1">
          {STATUS_FLOW.map((s, i) => {
            const reached = i <= currentIdx;
            return (
              <div key={s} className="flex flex-col items-center text-center">
                {reached ? <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" /> : <Circle className="w-5 h-5 text-[#525252]" />}
                <p className={`text-[10px] mt-1 ${reached ? 'text-white' : 'text-[#525252]'}`}>{s}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-2 text-sm">
          <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em] mb-2">DETAILS</p>
          <p className="text-[#A3A3A3]">Tenant: <span className="text-white">{r.tenant_name || '—'}</span></p>
          <p className="text-[#A3A3A3]">Tenant phone: <span className="text-white">{r.tenant_phone || '—'}</span></p>
          <p className="text-[#A3A3A3]">Submitted by: <span className="text-white">{r.submitted_by_name}</span></p>
          <p className="text-[#A3A3A3]">Created: <span className="text-white">{new Date(r.created_at).toLocaleString()}</span></p>
          <p className="text-[#A3A3A3]">Updated: <span className="text-white">{new Date(r.updated_at).toLocaleString()}</span></p>
          <p className="text-[#A3A3A3]">Technician: <span className="text-white">{r.assigned_technician_name || 'Not assigned'}</span></p>
        </div>
        <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3 text-sm">
          <div>
            <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em] mb-1">ISSUE</p>
            <p className="text-white whitespace-pre-wrap">{r.issue_description}</p>
          </div>
          {r.access_notes && <div>
            <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em] mb-1">ACCESS</p>
            <p className="text-white whitespace-pre-wrap">{r.access_notes}</p>
          </div>}
          {r.special_notes && <div>
            <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em] mb-1">NOTES</p>
            <p className="text-white whitespace-pre-wrap">{r.special_notes}</p>
          </div>}
        </div>
      </div>

      {/* Purchase Order */}
      <PoPanel request={r} mode="pm" onUpdated={setR} />

      {/* PM Authorization (Batch 3) */}
      <WoAuthorizationPanel workOrderId={r.id} onChange={() => pmApi.requestDetail(id).then(setR).catch(() => {})} />

      {/* Activity */}
      <JobTimeline workOrderId={r.id} mode="pm" />
      {(r.timeline || []).length > 0 && (
        <div className="bg-[#141414] border border-[#262626] rounded-md p-5">
          <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em] mb-3">ACTIVITY</p>
          <ul className="space-y-2 text-sm">
            {(r.timeline || []).map((t, i) => (
              <li key={i} className="flex justify-between gap-4 border-b border-[#262626] last:border-0 pb-2 last:pb-0">
                <span className="text-white">{t.status}{t.note ? ` — ${t.note}` : ''}</span>
                <span className="text-[#525252] text-xs">{t.at ? new Date(t.at).toLocaleString() : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default WorkOrderDetail;
